import React, { useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { type TooltipItem } from 'chart.js';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import { Chart } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';
import { KPI_BAR_SEQUENCE, KPI_SLATE } from './kpiChartPalette';

export interface ChangeAssignmentGroupRow {
  groupName: string;
  count: number;
  successPct: number;
}

export interface KpiChangeAssignmentGroupComboChartProps {
  rows: ChangeAssignmentGroupRow[];
  panelTitle?: string;
  emptyMessage?: string;
  /** Opens KPI drill-down table scoped to this assignment group + Change type. */
  onGroupClick?: (groupName: string) => void;
}

const DEFAULT_TITLE = 'Changes by Assignment Group w/ Success %';

const KpiChangeAssignmentGroupComboChart: React.FC<KpiChangeAssignmentGroupComboChartProps> = ({
  rows,
  panelTitle = DEFAULT_TITLE,
  emptyMessage = 'No changes for the selected filters.',
  onGroupClick,
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const labels = rows.map((r) => r.groupName);
  const counts = rows.map((r) => r.count);
  const successPcts = rows.map((r) => r.successPct);
  const maxSucc = useMemo(() => Math.max(0, ...successPcts, 0), [successPcts]);
  const y1Max = useMemo(() => {
    if (maxSucc <= 0) return 100;
    const padded = maxSucc * 1.15;
    return Math.min(100, Math.ceil(padded / 5) * 5);
  }, [maxSucc]);

  if (!rows.length) {
    return (
      <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onGroupClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null || el.datasetIndex !== 0) return;
    const idx = el.index;
    const row = rows[idx];
    if (!row || row.count <= 0) return;
    onGroupClick(row.groupName);
  };

  return (
    <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill titleClassName="text-center">
      <div className="relative min-h-[280px] w-full flex-1">
        <Chart
          ref={chartRef}
          type="bar"
          onClick={onGroupClick ? handleChartClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: 'Change count',
                data: counts,
                backgroundColor: labels.map((_, i) => KPI_BAR_SEQUENCE[i % KPI_BAR_SEQUENCE.length]),
                borderRadius: 6,
                barThickness: 28,
                yAxisID: 'y',
                order: 1,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: '#111827',
                  font: { weight: 600, size: 10 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
              {
                label: 'Success %',
                data: successPcts,
                type: 'line',
                borderColor: KPI_SLATE,
                backgroundColor: 'transparent',
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 5,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: KPI_SLATE,
                pointBorderWidth: 2,
                fill: false,
                yAxisID: 'y1',
                order: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 14,
                  color: '#475569',
                  font: { weight: 700, size: 9 },
                  formatter: (value: number, ctx: DataLabelContext) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    return `${Number.isFinite(v) ? v.toFixed(1) : '0'}%`;
                  },
                },
              },
            ] as any,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            onHover: (event, elements) => {
              const canvas = event.native?.target;
              if (!(canvas instanceof HTMLElement)) return;
              const el = elements[0];
              const idx = el?.index;
              const row = idx !== undefined ? rows[idx] : undefined;
              canvas.style.cursor = onGroupClick && row && row.count > 0 ? 'pointer' : 'default';
            },
            layout: { padding: { top: 36, right: 10, left: 4, bottom: 8 } },
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                align: 'center',
                labels: {
                  boxWidth: 18,
                  boxHeight: 10,
                  padding: 14,
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    return [
                      {
                        text: 'Change count',
                        fillStyle: KPI_BAR_SEQUENCE[0],
                        strokeStyle: 'transparent',
                        lineWidth: 0,
                        hidden: false,
                        index: 0,
                        datasetIndex: 0,
                      },
                      {
                        text: 'Success %',
                        fillStyle: '#ffffff',
                        strokeStyle: KPI_SLATE,
                        lineWidth: 1,
                        hidden: !ds[1],
                        index: 1,
                        datasetIndex: 1,
                      },
                    ] as any;
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    if (ctx.dataset.label === 'Success %') {
                      return `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}%`;
                    }
                    return `${ctx.dataset.label}: ${ctx.raw}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 35 },
              },
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                border: { display: false },
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: {
                  color: '#9ca3af',
                  maxTicksLimit: 8,
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                min: 0,
                max: y1Max,
                border: { display: false },
                grid: { drawOnChartArea: false },
                ticks: {
                  stepSize: y1Max <= 20 ? 5 : undefined,
                  maxTicksLimit: 8,
                  callback: (v) => `${v}%`,
                  color: '#9ca3af',
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiChangeAssignmentGroupComboChart;
